/**
 * FirestoreClient - Centralized Firestore CRUD operations
 * 
 * Provides a unified interface for all Firestore database operations with:
 * - Consistent error handling and logging
 * - Automatic retry logic with exponential backoff for transient failures
 * - Intelligent caching with TTL for successful reads
 * - Single source of truth for Firestore interactions
 * 
 * All methods are static and use the exported db instance from firebase.js
 * All async operations are wrapped with retry logic to handle transient failures.
 * Read operations cache results per CacheConfig TTL (only successful truthy values).
 */

import { doc, getDoc, setDoc, getDocs, collection, query, orderBy, limit, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase.js';
import errorHandler from '../handlers/ErrorHandler.js';
import withRetry from '../utils/withRetry.js';
import CacheManager from '../utils/CacheManager.js';

class FirestoreClient {
    /**
     * Get cached value or return null
     * @private
     */
    static #getCached(collectionName, identifier) {
        const cached = CacheManager.get(collectionName, identifier);
        if (cached !== null) {
            return cached;
        }
        return null;
    }
    
    /**
     * Get a single document by collection name and document ID
     * 
     * @async
     * @param {string} collectionName - Name of the collection
     * @param {string} docId - Document ID to retrieve
     * @returns {Promise<Object|null>} Document data or null if not found
     * @throws {Error} If the operation fails after all retries
     * @example
     * const product = await FirestoreClient.getDocument('products', 'pejas');
     */
    static getDocument = withRetry(
        async (collectionName, docId) => {
            try {
                // Validate parameters
                if (!collectionName || typeof collectionName !== 'string') {
                    throw new Error('collectionName is required and must be a string');
                }
                if (!docId || typeof docId !== 'string') {
                    throw new Error('docId is required and must be a string');
                }

                // Check cache first
                const cached = FirestoreClient.#getCached(collectionName, docId);
                if (cached !== null) {
                    return cached;
                }

                const docRef = doc(db, collectionName, docId);
                const docSnap = await getDoc(docRef);
                const data = docSnap.exists() ? docSnap.data() : null;
                
                // Cache only truthy values (not null, not empty objects, etc.)
                if (data) {
                    CacheManager.set(collectionName, docId, data);
                }
                
                return data;
            } catch (error) {
                const errorMessage = `Failed to get document: ${collectionName}/${docId}`;
                errorHandler.handle(error, { operation: 'getDocument', collectionName, docId }, 'error');
                throw error;
            }
        },
        'FirestoreClient.getDocument'
    );

    /**
     * Set or create a document in a collection
     * 
     * @async
     * @param {string} collectionName - Name of the collection
     * @param {string} docId - Document ID to set
     * @param {Object} data - Document data to write
     * @returns {Promise<void>}
     * @throws {Error} If the operation fails after all retries
     * @example
     * await FirestoreClient.setDocument('ideas', 'idea-123', { 
     *   title: 'New Idea', 
     *   description: '...', 
     *   createdAt: FirestoreClient.getServerTimestamp() 
     * });
     */
    static setDocument = withRetry(
        async (collectionName, docId, data) => {
            try {
                // Validate parameters
                if (!collectionName || typeof collectionName !== 'string') {
                    throw new Error('collectionName is required and must be a string');
                }
                if (!docId || typeof docId !== 'string') {
                    throw new Error('docId is required and must be a string');
                }
                if (!data || typeof data !== 'object' || Array.isArray(data)) {
                    throw new Error('data is required and must be a non-array object');
                }

                const docRef = doc(db, collectionName, docId);
                await setDoc(docRef, data);
                
                // Invalidate cache on write
                CacheManager.invalidate(collectionName, docId);
            } catch (error) {
                const errorMessage = `Failed to set document: ${collectionName}/${docId}`;
                errorHandler.handle(error, { operation: 'setDocument', collectionName, docId }, 'error');
                throw error;
            }
        },
        'FirestoreClient.setDocument'
    );

    /**
     * Query a collection with advanced filtering and ordering options
     * 
     * @async
     * @param {string} collectionName - Name of the collection to query
     * @param {Object} options - Query options
     * @param {string} [options.orderByField] - Field to order results by
     * @param {string} [options.order='asc'] - Order direction ('asc' or 'desc')
     * @param {number} [options.limitNum] - Maximum number of results
     * @param {string} [options.whereField] - Field for WHERE clause
     * @param {string} [options.whereOp] - Operator for WHERE clause (e.g., '==', '<', '>', '>=', '<=', '!=')
     * @param {*} [options.whereValue] - Value to filter by
     * @returns {Promise<Array<Object>>} Array of documents with id property
     * @throws {Error} If the query fails after all retries
     * @example
     * const products = await FirestoreClient.queryCollection('products', {
     *   whereField: 'isActive',
     *   whereOp: '==',
     *   whereValue: true,
     *   orderByField: 'displayOrder',
     *   order: 'asc'
     * });
     */
    static queryCollection = withRetry(
        async (collectionName, { orderByField, order = 'asc', limitNum, whereField, whereOp, whereValue } = {}) => {
            try {
                // Validate parameters
                if (!collectionName || typeof collectionName !== 'string') {
                    throw new Error('collectionName is required and must be a string');
                }
                if (order && !['asc', 'desc'].includes(order)) {
                    throw new Error('order must be "asc" or "desc"');
                }
                if (limitNum !== undefined && (!Number.isInteger(limitNum) || limitNum < 1)) {
                    throw new Error('limitNum must be a positive integer');
                }

                // Create identifier for caching query results
                const queryIdentifier = { whereField, whereOp, whereValue, orderByField, order, limitNum };
                
                // Check cache first
                const cached = FirestoreClient.#getCached(collectionName, queryIdentifier);
                if (cached !== null) {
                    return cached;
                }

                const constraints = [];
                
                if (whereField && whereOp && typeof whereValue !== 'undefined') {
                    constraints.push(where(whereField, whereOp, whereValue));
                }
                if (orderByField) {
                    constraints.push(orderBy(orderByField, order));
                }
                if (limitNum) {
                    constraints.push(limit(limitNum));
                }
                
                const q = query(collection(db, collectionName), ...constraints);
                const snapshot = await getDocs(q);
                const results = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
                
                // Always cache query results (arrays are never null)
                CacheManager.set(collectionName, queryIdentifier, results);
                
                return results;
            } catch (error) {
                const errorMessage = `Failed to query collection: ${collectionName}`;
                errorHandler.handle(error, { operation: 'queryCollection', collectionName, orderByField, order }, 'error');
                throw error;
            }
        },
        'FirestoreClient.queryCollection'
    );

    /**
     * Get a server-side timestamp
     * Useful for recording when documents are created or modified
     * 
     * @returns {Object} Firebase server timestamp value
     * @example
     * const data = {
     *   title: 'My Idea',
     *   createdAt: FirestoreClient.getServerTimestamp()
     * };
     */
    static getServerTimestamp() {
        return serverTimestamp();
    }

    /**
     * Find a single document by field value
     * Returns the first document matching the criteria
     * 
     * @async
     * @param {string} collectionName - Name of the collection
     * @param {string} field - Field name to search
     * @param {*} value - Value to match
     * @returns {Promise<Object|null>} First matching document or null if not found
     * @throws {Error} If the query fails after all retries
     * @example
     * const product = await FirestoreClient.findDocumentByField('products', 'slug', 'pejas');
     */
    static findDocumentByField = withRetry(
        async (collectionName, field, value) => {
            try {
                // Validate parameters
                if (!collectionName || typeof collectionName !== 'string') {
                    throw new Error('collectionName is required and must be a string');
                }
                if (!field || typeof field !== 'string') {
                    throw new Error('field is required and must be a string');
                }
                if (value === undefined) {
                    throw new Error('value is required (can be null, but not undefined)');
                }

                const fieldIdentifier = { field, value };
                
                // Check cache first
                const cached = FirestoreClient.#getCached(collectionName, fieldIdentifier);
                if (cached !== null) {
                    return cached;
                }

                const results = await FirestoreClient.queryCollection(collectionName, {
                    whereField: field,
                    whereOp: '==',
                    whereValue: value,
                    limitNum: 1
                });
                const result = results[0] || null;
                
                // Cache only truthy results
                if (result) {
                    CacheManager.set(collectionName, fieldIdentifier, result);
                }
                
                return result;
            } catch (error) {
                const errorMessage = `Failed to find document by field: ${collectionName}.${field} = ${value}`;
                errorHandler.handle(error, { operation: 'findDocumentByField', collectionName, field, value }, 'error');
                throw error;
            }
        },
        'FirestoreClient.findDocumentByField'
    );

    /**
     * Get a document from a subcollection within a parent document
     * Handles nested document structures (e.g., guidebooks/product-1/content/guide)
     * 
     * @async
     * @param {string} parentCollectionName - Name of the parent collection (e.g., 'guidebooks')
     * @param {string} parentDocId - ID of the parent document (e.g., 'product-1')
     * @param {string} subcollectionName - Name of the subcollection (e.g., 'content')
     * @param {string} docId - ID of the document to retrieve (e.g., 'guide')
     * @returns {Promise<Object|null>} Subcollection document or null if not found
     * @throws {Error} If the subcollection document cannot be retrieved after all retries
     * @example
     * const guideContent = await FirestoreClient.getSubcollectionDocument(
     *   'guidebooks', 
     *   'product-1', 
     *   'content', 
     *   'guide'
     * );
     */
    static getSubcollectionDocument = withRetry(
        async (parentCollectionName, parentDocId, subcollectionName, docId) => {
            try {
                // Validate parameters
                if (!parentCollectionName || typeof parentCollectionName !== 'string') {
                    throw new Error('parentCollectionName is required and must be a string');
                }
                if (!parentDocId || typeof parentDocId !== 'string') {
                    throw new Error('parentDocId is required and must be a string');
                }
                if (!subcollectionName || typeof subcollectionName !== 'string') {
                    throw new Error('subcollectionName is required and must be a string');
                }
                if (!docId || typeof docId !== 'string') {
                    throw new Error('docId is required and must be a string');
                }

                const subcollectionIdentifier = `${parentDocId}/${subcollectionName}/${docId}`;
                
                // Check cache first
                const cached = FirestoreClient.#getCached(parentCollectionName, subcollectionIdentifier);
                if (cached !== null) {
                    return cached;
                }

                const docRef = doc(db, parentCollectionName, parentDocId, subcollectionName, docId);
                const docSnap = await getDoc(docRef);
                const data = docSnap.exists() ? docSnap.data() : null;
                
                // Cache only truthy values
                if (data) {
                    CacheManager.set(parentCollectionName, subcollectionIdentifier, data);
                }
                
                return data;
            } catch (error) {
                const errorMessage = `Failed to get subcollection document: ${parentCollectionName}/${parentDocId}/${subcollectionName}/${docId}`;
                errorHandler.handle(error, { 
                    operation: 'getSubcollectionDocument',
                    parentCollectionName, 
                    parentDocId, 
                    subcollectionName, 
                    docId
                }, 'error');
                throw error;
            }
        },
        'FirestoreClient.getSubcollectionDocument'
    );
}

export default FirestoreClient;
